import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { MdOutlineFileUpload } from "react-icons/md";
import { ExternalIntegrationModel } from "../../services";
import { normalizeExternalIntegrationList } from "../../utils/externalIntegration";

function integrationRowKey(row) {
  return `${row._id}-${row.__legacyEndpointIndex ?? "single"}`;
}

function integrationDocumentId(row) {
  return row?._id ?? row?.id ?? "";
}

function SubmitToExternalIntegrationModalInner({ data, refetchData }) {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [integrations, setIntegrations] = useState([]);
  const [integrationKey, setIntegrationKey] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingList(true);
    (async () => {
      try {
        const res = await ExternalIntegrationModel.get();
        const raw = res.data?.data ?? res.data;
        const all = normalizeExternalIntegrationList(
          Array.isArray(raw) ? raw : []
        );
        const uploadOnly = all.filter(
          (row) => row.type === "upload_pdf_result" && row.url
        );
        if (!cancelled) {
          setIntegrations(uploadOnly);
          setIntegrationKey("");
        }
      } catch {
        if (!cancelled) setIntegrations([]);
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const selected = integrations.find(
    (i) => integrationRowKey(i) === integrationKey
  );

  const handleSubmit = async () => {
    if (!selected) {
      toast({
        description: "Choose an integration.",
        status: "warning",
        isClosable: true,
      });
      return;
    }
    const integrationId = integrationDocumentId(selected);
    if (!integrationId) {
      toast({
        description: "Invalid integration.",
        status: "error",
        isClosable: true,
      });
      return;
    }
    setSending(true);
    try {
      const body = { orderId: String(data._id) };
      if (selected.__legacyEndpointIndex != null) {
        body.endpointIndex = selected.__legacyEndpointIndex;
      }
      await ExternalIntegrationModel.execute(integrationId, body);
      toast({
        description: "PDF submitted successfully.",
        status: "success",
        isClosable: true,
      });
      onClose();
      if (refetchData) await refetchData();
    } catch (e) {
      const msg =
        e.response?.data?.message ||
        e.message ||
        "Request failed. Check the integration and try again.";
      toast({
        description: msg,
        status: "error",
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Tooltip label="Export PDF to external source" hasArrow>
        <IconButton
          type="button"
          onClick={onOpen}
          bgColor="primary.100"
          color="primary.main"
          fontSize="xl"
          icon={<MdOutlineFileUpload />}
          isRound
          aria-label="Export PDF to external source"
        />
      </Tooltip>
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Submit PDF to external system</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={4}>
              Sends this order&apos;s id to the portal; the server loads the
              result PDF and posts it to the URL configured for the integration.
            </Text>
            <FormControl>
              <FormLabel fontSize="sm">Integration</FormLabel>
              <Select
                placeholder={
                  loadingList ? "Loading…" : "Select integration…"
                }
                value={integrationKey}
                onChange={(e) => setIntegrationKey(e.target.value)}
                isDisabled={loadingList || integrations.length === 0}
                bg="white"
                borderRadius="lg"
              >
                {integrations.map((row) => (
                  <option
                    key={integrationRowKey(row)}
                    value={integrationRowKey(row)}
                  >
                    {row.name || "Unnamed"}
                  </option>
                ))}
              </Select>
            </FormControl>
            {integrations.length === 0 && !loadingList ? (
              <Text fontSize="sm" color="gray.500" mt={3}>
                Add an &quot;Upload PDF result&quot; integration under External
                APIs in settings.
              </Text>
            ) : null}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              bgColor="black"
              onClick={handleSubmit}
              isLoading={sending}
              loadingText="Sending…"
              isDisabled={!integrationKey || integrations.length === 0}
            >
              Send PDF
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default function SubmitToExternalIntegrationModal({
  data,
  refetchData,
  userRole,
}) {
  if (userRole !== "user") {
    return null;
  }
  return (
    <SubmitToExternalIntegrationModalInner
      data={data}
      refetchData={refetchData}
    />
  );
}
